import { useMemo } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import {
	fireOperationalAnalytics,
	fireTrackAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import type {
	RequestTypeIssueSecurityStatus,
	RequestTypeRestrictionStatus,
} from '@atlassian/jira-servicedesk-ticket-types-store/src/common/types.tsx';
import type { SupportedFeature } from '../../ui/config-button/config-modal/types.tsx';
import { useIssueSecurity } from '../issue-security/index.tsx';
import {
	isMisconfiguredMissingLevel,
	isMissingValueForRequiredField,
	isMissingValueForSelectOption,
} from '../issue-security/utils.tsx';
import { useRequestTypePermissions } from '../request-type-permissions/index.tsx';
import {
	PRINCIPAL_OVER_LIMITS,
	MISCONFIGURATION,
	UNKNOWN,
	type ApplyErrorType,
	type ApplyRequestTypeSecurityResult,
} from './types.tsx';

const PRINCIPALS_LIMIT = 50;

export const createApplySuccessResult = (
	newRestrictionStatus: RequestTypeRestrictionStatus | undefined,
	newIssueSecurityStatus: RequestTypeIssueSecurityStatus | undefined,
): ApplyRequestTypeSecurityResult => ({
	isSuccess: true,
	newRestrictionStatus,
	newIssueSecurityStatus,
});
export const createApplyErrorResult = (
	errorType: ApplyErrorType,
	error?: unknown,
): ApplyRequestTypeSecurityResult => ({ isSuccess: false, errorType, error });

export const useCombinedRequestTypeSecurity = (
	projectId: number,
	requestTypeExternalId: string,
	features: SupportedFeature[],
) => {
	const supportRequestTypePermission = features.includes('REQUEST_TYPE_PERMISSIONS');
	const supportIssueSecurity = features.includes('ISSUE_SECURITY');

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const {
		principals,
		prependPrincipals,
		removePrincipal,
		requestTypePermissionLoading,
		requestTypePermissionError,
		requestTypePermissionFetch,
		applyRequestTypePermission,
		applyRequestTypePermissionLoading,
	} = useRequestTypePermissions(projectId, requestTypeExternalId);

	const {
		issueSecurityState,
		makeSelection: makeIssueSecuritySelection,
		issueSecurityConfigLoading,
		issueSecurityConfigError,
		issueSecurityConfigFetch,
		applyIssueSecurity,
		applyIssueSecurityLoading,
		showError,
	} = useIssueSecurity(projectId, requestTypeExternalId);

	const configurationFetch = useMemo(() => {
		return async () => {
			supportRequestTypePermission && requestTypePermissionFetch();
			supportIssueSecurity && issueSecurityConfigFetch();
		};
	}, [
		issueSecurityConfigFetch,
		requestTypePermissionFetch,
		supportIssueSecurity,
		supportRequestTypePermission,
	]);

	const configurationLoading =
		(supportRequestTypePermission && requestTypePermissionLoading) ||
		(supportIssueSecurity && issueSecurityConfigLoading);

	const configurationErrors: Error[] = [
		...(supportRequestTypePermission && requestTypePermissionError
			? [requestTypePermissionError]
			: []),
		...(supportIssueSecurity && issueSecurityConfigError ? [issueSecurityConfigError] : []),
	];

	const fireAnalytics = (result: 'succeed' | 'fail', error?: unknown) => {
		const attributes = {
			error: error?.toString(),
		};
		return result === 'succeed'
			? fireTrackAnalytics(
					createAnalyticsEvent({}),
					'combinedRequestTypeSecurity updated',
					attributes,
				)
			: fireOperationalAnalytics(
					createAnalyticsEvent({}),
					'combinedRequestTypeSecurity updateFailed',
					attributes,
				);
	};

	const applyConfiguration: () => Promise<ApplyRequestTypeSecurityResult> = async () => {
		if (supportRequestTypePermission && principals.length > PRINCIPALS_LIMIT) {
			return createApplyErrorResult(PRINCIPAL_OVER_LIMITS);
		}

		if (
			supportIssueSecurity &&
			issueSecurityState &&
			(isMisconfiguredMissingLevel(issueSecurityState) ||
				isMissingValueForRequiredField(issueSecurityState) ||
				isMissingValueForSelectOption(issueSecurityState))
		) {
			showError();
			return createApplyErrorResult(MISCONFIGURATION);
		}

		try {
			const [newRestrictionStatus, newIssueSecurityStatus] = await Promise.all([
				supportRequestTypePermission ? applyRequestTypePermission() : Promise.resolve(undefined),
				supportIssueSecurity ? applyIssueSecurity() : Promise.resolve(undefined),
			]);
			fireAnalytics('succeed');
			return createApplySuccessResult(newRestrictionStatus, newIssueSecurityStatus);
		} catch (e) {
			fireAnalytics('fail', e);
			return createApplyErrorResult(UNKNOWN, e);
		}
	};

	const applyConfigurationLoading =
		(supportRequestTypePermission ? applyRequestTypePermissionLoading : false) ||
		(supportIssueSecurity ? applyIssueSecurityLoading : false);

	return {
		principals,
		prependPrincipals,
		removePrincipal,
		issueSecurityState,
		makeIssueSecuritySelection,
		configurationFetch,
		configurationLoading,
		configurationErrors,
		applyConfiguration,
		applyConfigurationLoading,
	};
};
